import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { AwardIcon, SearchCheckIcon, UserCheckIcon, ClipboardCheckIcon, FileTextIcon, KeyIcon, UsersIcon } from 'lucide-react';
function FlipCard({
  step,
  index,
  inView
}: {
  step: any;
  index: number;
  inView: boolean;
}) {
  const [isFlipped, setIsFlipped] = useState(false);
  // Different entrance directions for staggered effect
  const directions = [{
    x: -100,
    y: -50
  }, {
    x: 100,
    y: -50
  }, {
    x: -100,
    y: 50
  }, {
    x: 100,
    y: 50
  }, {
    x: -100,
    y: -50
  }, {
    x: 100,
    y: 50
  }];
  return <motion.div className="relative h-64 md:h-80 cursor-pointer touch-manipulation" initial={{
    x: directions[index].x,
    y: directions[index].y,
    opacity: 0
  }} animate={inView ? {
    x: 0,
    y: 0,
    opacity: 1
  } : {}} transition={{
    duration: 0.8,
    delay: index * 0.15,
    type: 'spring',
    stiffness: 100
  }} onMouseEnter={() => setIsFlipped(true)} onMouseLeave={() => setIsFlipped(false)} onClick={() => setIsFlipped(!isFlipped)} style={{
    perspective: '1000px'
  }} data-id="element-19">
      {/* Glow effect */}
      <motion.div className="absolute inset-0 rounded-xl md:rounded-2xl bg-gradient-to-br from-[#357beb] to-[#2868d9] opacity-0 blur-xl" animate={{
      opacity: isFlipped ? 0.4 : 0
    }} transition={{
      duration: 0.3
    }} data-id="element-20" />
      {/* Card container */}
      <motion.div className="relative w-full h-full" animate={{
      rotateY: isFlipped ? 180 : 0
    }} transition={{
      duration: 0.6,
      type: 'spring',
      stiffness: 100
    }} style={{
      transformStyle: 'preserve-3d'
    }} data-id="element-21">
        {/* Front of card */}
        <div className="absolute inset-0 bg-white rounded-xl md:rounded-2xl shadow-xl p-4 md:p-8 flex flex-col items-center justify-center gap-3 md:gap-6 border-2 border-gray-100" style={{
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden'
      }} data-id="element-22">
          <div className="text-5xl md:text-7xl font-bold text-[#357beb]/20" data-id="element-23">
            {step.number}
          </div>
          <div className="p-4 md:p-6 bg-gradient-to-br from-[#357beb] to-[#2868d9] rounded-xl md:rounded-2xl shadow-lg" data-id="element-24">
            <step.icon className="w-8 h-8 md:w-12 md:h-12 text-white" data-id="element-25" />
          </div>
          <h3 className="text-base md:text-xl font-bold text-[#121c53] text-center leading-tight px-2" data-id="element-26">
            {step.title}
          </h3>
        </div>
        {/* Back of card */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#357beb] to-[#2868d9] rounded-xl md:rounded-2xl shadow-xl p-4 md:p-8 flex flex-col items-center justify-center" style={{
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        transform: 'rotateY(180deg)'
      }} data-id="element-27">
          <div className="mb-3 md:mb-4 p-3 md:p-4 bg-white/20 rounded-lg md:rounded-xl backdrop-blur-sm" data-id="element-28">
            <step.icon className="w-6 h-6 md:w-8 md:h-8 text-white" data-id="element-29" />
          </div>
          <p className="text-white text-center leading-relaxed text-xs md:text-sm px-2" data-id="element-30">
            {step.description}
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
export function About() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const steps = [{
    icon: SearchCheckIcon,
    number: '01',
    title: 'Analiză și verificare imobil',
    description: 'Furnizăm toate informațiile necesare despre proprietate — acte, regim juridic, documentație cadastrală și tehnică — pentru ca fiecare investitor să poată evalua în deplină siguranță potențialul real al activului.'
  }, {
    icon: UserCheckIcon,
    number: '02',
    title: 'Asistență și consultanță personalizată',
    description: 'Oferim evaluări de piață, analize de randament și strategii personalizate de investiție, adaptate obiectivelor fiecărui client.'
  }, {
    icon: ClipboardCheckIcon,
    number: '03',
    title: 'Intermediere vizite și prezentări tehnice',
    description: 'Organizăm vizite comerciale și tehnice la imobil, oferind acces direct la informații relevante despre starea clădirii, infrastructură și potențialul de reconversie sau dezvoltare.'
  }, {
    icon: FileTextIcon,
    number: '04',
    title: 'Suport documentar și pregătirea actelor',
    description: 'Colaborăm cu notari, avocați și specialiști tehnici pentru pregătirea completă a documentelor necesare semnării. Ne asigurăm că toate etapele sunt clare, corecte și conforme cu legislația în vigoare.'
  }, {
    icon: KeyIcon,
    number: '05',
    title: 'Asistență la semnare și predare imobil',
    description: 'Oferim suport până la semnarea contractului final și coordonăm procesul de predare a imobilului, însoțit de documentația completă și actualizată.'
  }, {
    icon: UsersIcon,
    number: '06',
    title: 'Conectare cu furnizori și specialiști',
    description: 'Te conectăm cu toți furnizorii, specialiștii și networkingul nostru pentru o experiență completă de investiție sigură — de la arhitecți și constructori până la consultanți fiscali și juridici.'
  }];
  return <section className="py-12 md:py-24 px-4 md:px-6 lg:px-16 bg-gradient-to-b from-white to-gray-50" data-id="element-31">
      <div className="max-w-7xl mx-auto" ref={ref} data-id="element-32">
        {/* Header Section */}
        <motion.div className="text-center mb-8 md:mb-20" initial={{
        y: -30,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8
      }} data-id="element-33">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#121c53] mb-3 md:mb-6 leading-tight px-4" data-id="element-34">
            Partenerul tău complet în fiecare pas al{' '}
            <span className="text-[#357beb]" data-id="element-35">tranzacției imobiliare</span>
          </h2>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto font-medium px-4" data-id="element-36">
            cautlocuinta.ro – Expertiză, transparență și rezultate în real
            estate.
          </p>
        </motion.div>
        {/* Main Content */}
        <motion.div className="bg-white rounded-xl md:rounded-2xl shadow-xl p-6 md:p-8 lg:p-12 mb-8 md:mb-16" initial={{
        y: 30,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.2
      }} data-id="element-37">
          <p className="text-base md:text-lg text-gray-700 leading-relaxed" data-id="element-38">
            La <strong className="text-[#121c53]" data-id="element-39">cautlocuinta.ro</strong>, nu
            ne limităm la publicarea unei oferte — suntem activi în fiecare
            etapă a procesului de vânzare și achiziție. Oferim servicii complete
            de consultanță și intermediere imobiliară, asigurând o tranzacție
            sigură, transparentă și eficientă pentru toate părțile implicate.
          </p>
        </motion.div>
        {/* Flip Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-16" data-id="element-40">
          {steps.map((step, index) => <FlipCard key={index} step={step} index={index} inView={inView} data-id="element-41" />)}
        </div>
        {/* Closing Statement */}
        <motion.div className="bg-gradient-to-br from-[#121c53] to-[#1a3a6b] rounded-xl md:rounded-2xl p-6 md:p-10 lg:p-14 text-center shadow-2xl" initial={{
        scale: 0.95,
        opacity: 0
      }} animate={inView ? {
        scale: 1,
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.8
      }} data-id="element-42">
          <p className="text-white text-base md:text-xl lg:text-2xl leading-relaxed mb-4 md:mb-6" data-id="element-43">
            Cu o experiență extinsă în tranzacționarea de proprietăți și
            proiecte imobiliare de top,{' '}
            <strong className="text-[#357beb]" data-id="element-44">cautlocuinta.ro</strong> este
            partenerul ideal pentru investitori, dezvoltatori și proprietari
            care apreciază profesionalismul, discreția și eficiența.
          </p>
          <p className="text-gray-200 text-sm md:text-lg italic" data-id="element-45">
            Fiecare colaborare este tratată cu seriozitate, iar fiecare
            tranzacție – ca o poveste de succes.
          </p>
        </motion.div>
      </div>
    </section>;
}